import { useForm } from "react-hook-form";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal";
import { DefaultFormMultiAutocomplete } from "../../../../../../common/components/default_form_multi_autocomplete";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { StyledDataGrid } from "../../../../../../common/components/styled_data_grid";
import { useEffect, useMemo } from "react";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { IconContainerProps, Rating, Stack, styled, TablePagination } from "@mui/material";
import { CriterioAvaliacaoFornecedor } from "../../../../domain/models/criterio_avaliacao_fornecedor";
import { AvaliacaoFornecedorForm, AvaliacaoFornecedorSchema } from "../../../schemas/ProdutosServicos_lista/avaliacao_fornecedor";
import { FuncionarioDropDown } from "../../../../../../common/domain/models/funcionario_dropdown";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { FornecedorDropDown } from "../../../../../../common/domain/models/fornecedor_dropdown";
import { FuncionarioInformation } from "../../../../../../common/domain/models/funcionario_information";

import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";

/**
 * Icons
 */

const customIcons: {
  [index: string]: {
    textColor: string;
    icon: React.ReactElement<unknown>;
    label: string;
  };
} = {
  1: {
    textColor: 'text-red-500',
    icon: <SentimentVeryDissatisfiedIcon className="text-red-500" />,
    label: 'Very Dissatisfied',
  },
  2: {
    textColor: 'text-yellow-500',
    icon: <SentimentSatisfiedIcon className="text-yellow-500" />,
    label: 'Neutral',
  },
  3: {
    textColor: 'text-green-600',
    icon: <SentimentVerySatisfiedIcon className="text-green-600" />,
    label: 'Very Satisfied',
  },
};

function IconContainer(props: IconContainerProps) {
  const { value, ...other } = props;
  const isSelected = other.className?.includes('MuiRating-iconFilled');
  return (
    <span {...other}>
      <div className="flex flex-col items-center">
        <span className="flex">
          {customIcons[value].icon}
        </span>
        <span className={`${isSelected ? customIcons[value].textColor : ''} text-xs`}>
          {typeof value === 'number' && value}
        </span>
      </div >
    </span >
  );
}

const StyledRating = styled(Rating)(({ theme }) => ({
  '& .MuiRating-iconEmpty .MuiSvgIcon-root': {
    color: theme.palette.action.disabled,
  },
}));

/**
 * AvaliacaoFornecedorModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  rowSelectionModel: GridRowSelectionModel | undefined,
  funcionarioInformation: FuncionarioInformation | null;
  fornecedoresDropdown: FornecedorDropDown[];
  centroCustosDropdown: CentroCustoDropDown[];
  funcionariosDropdown: FuncionarioDropDown[];
  criteriosAvaliacaoFornecedor: CriterioAvaliacaoFornecedor[];
  onClose: () => void;
  onSubmit: (form: AvaliacaoFornecedorForm) => void;
};

export const AvaliacaoFornecedorModal = ({
  open,
  saving,
  loading,
  rowSelectionModel,
  funcionarioInformation,
  fornecedoresDropdown,
  centroCustosDropdown,
  funcionariosDropdown,
  criteriosAvaliacaoFornecedor,
  onClose,
  onSubmit,
}: Props) => {

  const hookForm = useForm<AvaliacaoFornecedorForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(AvaliacaoFornecedorSchema),
  });

  const [criterios] = hookForm.watch(['criteriosAvaliacaoFornecedor']);

  const columns = useMemo((): GridColDef[] => {
    return [
      {
        field: "descricao",
        filterable: false,
        headerName: "Item",
        flex: 1,
        minWidth: 120,
        sortable: false,
        renderCell: ({ value }) => {
          return (
            <div className="py-2">{value}</div>
          );
        }
      },
      {
        field: "nota",
        filterable: false,
        headerName: "Selecione a Nota",
        width: 150,
        sortable: false,
        align: 'center',
        valueGetter: (_, rows) => {
          return [rows['idCriterioAvaliacaoFornecedor'] as number, rows['nota'] as number];
        },
        renderCell: ({ value: [idCriterioAvaliacaoFornecedor, nota] }) => {
          return (
            <div className="flex flex-col justify-center items-center py-2">
              <StyledRating
                className="w-full h-full"
                getLabelText={(value: number) => customIcons[value].label}
                highlightSelectedOnly={true}
                value={nota}
                max={3}
                onChange={(_, newNota) => {
                  const notas = hookForm.getValues('criteriosAvaliacaoFornecedor');
                  const notaIndex = notas.findIndex(x => x.idCriterioAvaliacaoFornecedor === idCriterioAvaliacaoFornecedor);

                  if (notaIndex >= 0) {
                    notas[notaIndex].nota = newNota ?? 0;
                    hookForm.setValue('criteriosAvaliacaoFornecedor', [...notas]);
                  }
                }}
                slotProps={{
                  root: {
                    className: "flex flex-row justify-center items-center gap-2",
                  },
                  icon: {
                    component: IconContainer,
                  }
                }}
              />
            </div>
          );
        },
      },
    ];
  }, [
    hookForm,
  ]);

  useEffect(() => {
    if (open) {
      hookForm.reset();

      if (rowSelectionModel && rowSelectionModel.ids) {
        if (rowSelectionModel.ids.size > 0) {
          const idsFornecedores = Array.from(rowSelectionModel.ids).map(x => {
            if (typeof x === 'string') {
              return Number(x);
            }
            if (typeof x === 'number') {
              return x;
            }
            throw new Error('Id inspeção obra invalido!');
          });
          hookForm.setValue('fornecedores', idsFornecedores);
        }
      }

      if (funcionarioInformation && funcionarioInformation.idFuncionario) {
        hookForm.setValue('avaliadoPor', funcionarioInformation.idFuncionario);
      }

      hookForm.setValue('data', new Date(Date.now()));
      hookForm.setValue('criteriosAvaliacaoFornecedor', [
        ...criteriosAvaliacaoFornecedor
          .slice()
          .sort((a, b) => {
            if (a.ordem && b.ordem) return a.ordem - b.ordem;
            if (a.ordem) return 1;
            if (b.ordem) return -1;
            return 0;
          })
          .map(x => ({
            idCriterioAvaliacaoFornecedor: x.idCriterioAvaliacaoFornecedor,
            descricao: x.descricao ?? '',
            nota: x.nota ?? 0,
            ordem: x.ordem ?? 0,
          })),
      ]);
    }
  }, [
    open,
    funcionarioInformation,
    criteriosAvaliacaoFornecedor,
    rowSelectionModel,
    hookForm,
  ]);

  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      width="65rem"
      title="Avaliação de Fornecedor"
      onClose={onClose}
      onSubmit={hookForm.handleSubmit(onSubmit)}>
      <FormContainer title="Informações" arrangement="fullRow" border={false} paddingTop="normal" paddingBottom="small">
        <div className='w-full grid grid-cols-1 md:grid-cols-2 gap-4'>
          <div className="w-full col-span-1 md:col-span-2">
            <DefaultFormMultiAutocomplete
              name='fornecedores'
              label='Fornecedores'
              required={true}
              options={fornecedoresDropdown}
              getOptionKey={x => x.idFornecedor}
              getOptionLabel={x => x.nomeFantasia}
              reactHookForm={hookForm}
            />
          </div>

          <DefaultFormAutocomplete
            name='avaliadoPor'
            label='Avaliado Por'
            required={true}
            getOptionDisabled={x => !x.canBeSelected}
            options={funcionariosDropdown}
            getOptionKey={x => x.idFuncionario}
            getOptionLabel={x => x.nome}
            reactHookForm={hookForm}
          />

          <FormDatePickerInput
            name='data'
            label='Data'
            required={true}
            reactHookForm={hookForm}
          />

          <DefaultFormAutocomplete
            name='centroCusto'
            label='Centro de Custo'
            required={true}
            options={centroCustosDropdown}
            getOptionKey={x => x.id}
            getOptionLabel={x => x.title}
            reactHookForm={hookForm}
          />

          <DefaultFormInput
            name='nroNotaFiscal'
            label='N° nota fiscal'
            required={true}
            reactHookForm={hookForm}
          />
        </div>
      </FormContainer>

      <FormContainer title="Critérios de Avaliação" arrangement="fullRow" paddingTop="small" paddingBottom="small">
        <>
          <Stack>
            <StyledDataGrid
              tableName="fornecedor_avaliacao_fornecedor_modal"
              height='22.5rem'
              getRowHeight={() => 'auto'}
              disableColumnMenu={false}
              disableRowSelectionOnClick={true}
              disableColumnSorting={true}
              disableColumnResize={true}
              getRowId={(row: CriterioAvaliacaoFornecedor) => row.idCriterioAvaliacaoFornecedor}
              columns={columns}
              loading={saving}
              rows={criterios ?? []}
              rowCount={criterios?.length ?? 0}
              showToolbar={false}
              pageSizeOptions={[100]}
              paginationMode="server"
              sortModel={[{ field: 'ordem', sort: 'asc' }]}
              paginationModel={{
                page: 1,
                pageSize: 100,
              }}
            />

            <div className="w-full flex flex-row justify-between">
              <div>
                {hookForm.formState.errors.root &&
                  <span className="text-red-500 text-sm">
                    {hookForm.formState.errors.root.message}
                  </span>
                }
              </div>

              <TablePagination
                component="div"
                count={5}
                page={0}
                rowsPerPageOptions={[100]}
                onPageChange={(_, p) => { }}
                rowsPerPage={100}
              />
            </div>
          </Stack>
        </>
      </FormContainer>
      <FormContainer title="Observação" arrangement="fullRow" paddingTop="small" paddingBottom="normal">
        <div className="mt-2 flex flex-col">
          <DefaultFormInput
            name='observacao'
            label='Observações'
            multiline={true}
            rows={5}
            reactHookForm={hookForm}
          />
          <span className="text-red-500 text-sm">
            * Em caso de nota 1 ou 2, inserir o motivo da nota e qual será a tratativa.
          </span>
        </div>
      </FormContainer>
    </BaseFormControlledModal>
  );
}